import React, { useState, useEffect, useRef } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { Phone, PhoneOff, Mic, MicOff, Clock, Smartphone, Monitor } from 'lucide-react';
import { Device, Call } from '@twilio/voice-sdk';

interface CallModalProps {
  isOpen: boolean;
  onClose: () => void;
  customerNumber: string;
  apiUrl: string;
  user: any;
  userProfile: any;
  selectedUserPhone?: string; // NEW: Selected Twilio number for calling
  autoSwitchToBrowserCall?: boolean;
}

interface CallState {
  status: 'idle' | 'connecting' | 'connected' | 'disconnected' | 'error';
  duration: number;
  isMuted: boolean;
}

type CallType = 'proxy' | 'browser';

const CallModal: React.FC<CallModalProps> = ({ isOpen, onClose, customerNumber, apiUrl, user, userProfile, selectedUserPhone, autoSwitchToBrowserCall = false }) => {
  const [activeTab, setActiveTab] = useState<CallType>('proxy');
  const [proxyNumber, setProxyNumber] = useState('');
  const [calling, setCalling] = useState(false);
  const [callSuccess, setCallSuccess] = useState(false);
  
  // Browser call state
  const [callState, setCallState] = useState<CallState>({
    status: 'idle',
    duration: 0,
    isMuted: false
  });
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState(false);
  
  const deviceRef = useRef<Device | null>(null);
  const callRef = useRef<Call | null>(null);
  const durationIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const pendingCallParams = useRef<{ to: string, from: string } | null>(null);

  // Initialize proxy number when modal opens
  useEffect(() => {
    if (isOpen) {
      setProxyNumber(userProfile?.personalNumber || '');
      setCallSuccess(false);
      setCalling(false);
      setError('');
      setCallState({
        status: 'idle',
        duration: 0,
        isMuted: false
      });
    }
  }, [isOpen, userProfile]);

  // Auto-switch to browser call tab if requested
  useEffect(() => {
    if (isOpen && autoSwitchToBrowserCall) {
      setActiveTab('browser');
    }
  }, [isOpen, autoSwitchToBrowserCall, customerNumber]);

  // Initialize Twilio Voice SDK for browser calls
  useEffect(() => {
    if (!isOpen || activeTab !== 'browser') return;

    // Add global error handler for unhandled errors
    const handleGlobalError = (event: ErrorEvent) => {
      console.error('Global error caught:', event.error);
    };

    window.addEventListener('error', handleGlobalError);

    // Cleanup function
    return () => {
      window.removeEventListener('error', handleGlobalError);
    };
  }, [isOpen, activeTab]);

  // Initialize Twilio Device for browser calls
  const initializeDevice = async (token: string) => {
    try {
      if (deviceRef.current) {
        deviceRef.current.destroy();
        deviceRef.current = null;
      }

      deviceRef.current = new Device(token);
      
      // Register the device
      await deviceRef.current.register();
      
      // Try immediate connection as fallback
      setTimeout(() => {
        if (pendingCallParams.current) {
          initiateCallDirectly();
        }
      }, 1000); // 1 second delay to allow registration
    } catch (error: any) {
      console.error('Error initializing Twilio Device:', error);
      throw error;
    }

    deviceRef.current.on('registered', async () => {
      if (pendingCallParams.current) {
        try {
          const { to, from } = pendingCallParams.current;
          setCallState(prev => ({ ...prev, status: 'connecting' }));
          const call = await deviceRef.current!.connect({ params: { To: to, From: from } });
          callRef.current = call;
          pendingCallParams.current = null;
          
          // Set up call event handlers
          setupCallEventHandlers(call);
        } catch (error: any) {
          console.error('Error initiating call:', error);
          setError(`Call error: ${error.message || 'Failed to connect'}`);
          setCallState(prev => ({ ...prev, status: 'error' }));
        }
      }
    });

    // Also listen for 'ready' event as fallback
    deviceRef.current.on('ready', async () => {
      if (pendingCallParams.current) {
        try {
          const { to, from } = pendingCallParams.current;
          setCallState(prev => ({ ...prev, status: 'connecting' }));
          const call = await deviceRef.current!.connect({ params: { To: to, From: from } });
          callRef.current = call;
          pendingCallParams.current = null;
          
          // Set up call event handlers
          setupCallEventHandlers(call);
        } catch (error: any) {
          console.error('Error initiating call:', error);
          setError(`Call error: ${error.message || 'Failed to connect'}`);
          setCallState(prev => ({ ...prev, status: 'error' }));
        }
      }
    });
    

    deviceRef.current.on('error', (error: any) => {
      console.error('Twilio Device error:', error);
      
      // Check for specific error types
      if (error.code === 31007 && error.message.includes('Client version not supported')) {
        setError(`SDK Version Error: Please refresh the page to load the latest Twilio SDK version`);
      } else if (error.message && error.message.includes('JWT')) {
        setError(`JWT Error: ${error.message}`);
      } else if (error.message && error.message.includes('token')) {
        setError(`Token Error: ${error.message}`);
      } else {
        setError(`Device error: ${error.message || 'Unknown device error'}`);
      }
      setCallState(prev => ({ ...prev, status: 'error' }));
    });
    
    deviceRef.current.on('incoming', (_call: Call) => {
      // Handle incoming calls if needed
    });

    // Note: Call event handlers are now set up in setupCallEventHandlers function
    
    deviceRef.current.on('disconnect', () => {
      setCallState(prev => ({ ...prev, status: 'disconnected' }));
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
      }
      setTimeout(() => {
        onClose();
      }, 2000);
    });
  };

  // Set up call event handlers
  const setupCallEventHandlers = (call: Call) => {
    call.on('accept', () => {
      setCallState(prev => ({ ...prev, status: 'connected' }));
      
      const startTime = Date.now();
      durationIntervalRef.current = setInterval(() => {
        const duration = Math.floor((Date.now() - startTime) / 1000);
        setCallState(prev => ({ ...prev, duration }));
      }, 1000);
    });

    call.on('disconnect', () => {
      setCallState(prev => ({ ...prev, status: 'disconnected' }));
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
      }
      setTimeout(() => {
        onClose();
      }, 2000);
    });

    call.on('error', (callError: any) => {
      console.error('Call error:', callError);
      setError(`Call error: ${callError.message || 'Unknown error occurred'}`);
      setCallState(prev => ({ ...prev, status: 'error' }));
      setTimeout(() => {
        onClose();
      }, 3000);
    });
  };

  // Direct call initiation (fallback)
  const initiateCallDirectly = async () => {
    if (!pendingCallParams.current || !deviceRef.current) return;
    
    try {
      const { to, from } = pendingCallParams.current;
      setCallState(prev => ({ ...prev, status: 'connecting' }));
      const call = await deviceRef.current.connect({ params: { To: to, From: from } });
      callRef.current = call;
      pendingCallParams.current = null;
      
      // Set up call event handlers
      setupCallEventHandlers(call);
    } catch (error: any) {
      console.error('Error in direct call initiation:', error);
      setError(`Call error: ${error.message || 'Failed to connect'}`);
      setCallState(prev => ({ ...prev, status: 'error' }));
    }
  };

  // Proxy call functions
  const initiateProxyCall = async () => {
    if (!apiUrl || !user || !customerNumber) {
      return;
    }

    if (!proxyNumber || proxyNumber.trim() === '') {
      setError('Please enter a proxy number (your phone number) to initiate the call.');
      return;
    }

    setCalling(true);
    setCallSuccess(false);
    setError('');

    try {
      let jwtToken: string | undefined;
      try {
        const session = await fetchAuthSession();
        jwtToken = session.tokens?.idToken?.toString();
        
        if (!jwtToken) {
          throw new Error('No authentication token available');
        }
      } catch (error) {
        throw new Error('Authentication failed');
      }

      const response = await fetch(`${apiUrl}/app/calls/initiate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${jwtToken}`,
        },
        body: JSON.stringify({
          to: customerNumber,
          defaultDialedNumber: proxyNumber,
          fromPhoneNumber: selectedUserPhone // Pass the selected Twilio number
        })
      });

      if (response.ok) {
        await response.json();
        setCallSuccess(true);
        setTimeout(() => {
          onClose();
          setCallSuccess(false);
        }, 1500);
      } else {
        throw new Error('Failed to initiate call');
      }
    } catch (error: any) {
      setError(error.message || 'Failed to initiate call');
    } finally {
      setCalling(false);
    }
  };

  // Browser call functions
  const initiateBrowserCall = async () => {
    if (!apiUrl || !user || !customerNumber) {
      setError('Missing required parameters');
      return;
    }

    setLoading(true);
    setError('');

    try {
      let jwtToken: string | undefined;
      try {
        const session = await fetchAuthSession();
        jwtToken = session.tokens?.idToken?.toString();
        
        if (!jwtToken) {
          throw new Error('No authentication token available');
        }
      } catch (error) {
        console.error('Authentication error:', error);
        throw new Error('Authentication failed');
      }
      const response = await fetch(`${apiUrl}/app/voice/token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${jwtToken}`,
        },
        body: JSON.stringify({
          to: customerNumber,
          fromPhoneNumber: selectedUserPhone // Pass the selected Twilio number
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Voice token error response:', errorData);
        throw new Error(errorData.error || 'Failed to generate voice token');
      }

      const { token, toPhoneNumber, fromPhoneNumber } = await response.json();

      if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost') {
        throw new Error('Browser calls require HTTPS. Please use a secure connection.');
      }

      pendingCallParams.current = { to: toPhoneNumber || customerNumber, from: fromPhoneNumber };
      await initializeDevice(token);
    } catch (err: any) {
      console.error('Browser call initiation error:', err);
      setError(err.message || 'Failed to generate voice token');
      setCallState(prev => ({ ...prev, status: 'error' }));
    } finally {
      setLoading(false);
    }
  };

  const endBrowserCall = () => {
    if (callRef.current) {
      callRef.current.disconnect();
    }
    if (deviceRef.current) {
      deviceRef.current.destroy();
    }
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
    }
    setCallState({
      status: 'idle',
      duration: 0,
      isMuted: false
    });
    setError('');
  };

  const toggleMute = () => {
    if (callRef.current) {
      if (callState.isMuted) {
        callRef.current.mute(false);
      } else {
        callRef.current.mute(true);
      }
      setCallState(prev => ({ ...prev, isMuted: !prev.isMuted }));
    }
  };



  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Cleanup on unmount or close
  useEffect(() => {
    return () => {
      if (callRef.current) {
        callRef.current.disconnect();
        callRef.current = null;
      }
      if (deviceRef.current) {
        deviceRef.current.destroy();
        deviceRef.current = null;
      }
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
      }
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="rounded-lg border bg-card text-card-foreground shadow-lg w-full max-w-md">
        <div className="flex flex-col space-y-1.5 p-4 sm:p-6 border-b">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold leading-none tracking-tight flex items-center space-x-2">
              <Phone className="h-5 w-5" />
              <span>Initiate Call</span>
            </h3>
            <button
              onClick={onClose}
              className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-4 sm:p-6 space-y-4">
          {/* Tab Navigation */}
          <div className="flex space-x-1 bg-muted p-1 rounded-lg">
            <button
              onClick={() => setActiveTab('proxy')}
              className={`flex-1 flex items-center justify-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'proxy'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Smartphone className="h-4 w-4" />
              <span>Relay Call</span>
            </button>
            <button
              onClick={() => setActiveTab('browser')}
              className={`flex-1 flex items-center justify-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'browser'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Monitor className="h-4 w-4" />
              <span>Browser Call</span>
            </button>
          </div>

          {/* Customer Number Display */}
          <div className="text-center">
            <div className="text-lg font-semibold">{customerNumber}</div>
            <div className="text-sm text-muted-foreground">
              {activeTab === 'proxy' ? 'Will call your phone first, then connect to the client' : 'Direct browser-to-phone call'}
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          {/* Proxy Call Tab */}
          {activeTab === 'proxy' && (
            <>
              {callSuccess ? (
                <div className="text-center">
                  <div className="text-green-600 font-semibold mb-2">✅ Call initiated successfully!</div>
                  <p className="text-sm text-muted-foreground">Your phone will ring shortly...</p>
                </div>
              ) : (
                <>
                  <div className="space-y-2">
                    <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                      Relay Number (Your Mobile Phone)
                    </label>
                    <input
                      type="text"
                      value={proxyNumber}
                      onChange={e => setProxyNumber(e.target.value)}
                      placeholder={userProfile?.personalNumber ? userProfile.personalNumber : "Enter your phone number (e.g., +1234567890)"}
                      className="flex h-10 w-full rounded-md border border-input bg-muted px-3 py-2 text-sm"
                    />
                    <p className="text-xs text-muted-foreground">
                      {userProfile?.personalNumber 
                        ? "This number will ring first, then connect you to the client. The client will never see this phone number."
                        : "Enter your phone number. This will ring first, then connect you to the client. The client will never see this phone number."
                      }
                    </p>
                  </div>
                  <div className="flex justify-end space-x-2">
                    <button
                      onClick={onClose}
                      className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-9 px-3"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={initiateProxyCall}
                      disabled={calling || !proxyNumber.trim() || !customerNumber}
                      className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-9 px-4"
                    >
                      {calling ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      ) : (
                        <Phone className="h-4 w-4 mr-2" />
                      )}
                      {calling ? 'Initiating...' : 'Call'}
                    </button>
                  </div>
                </>
              )}
            </>
          )}

          {/* Browser Call Tab */}
          {activeTab === 'browser' && (
            <>
              <div className="text-center mb-6">
                <div className="text-sm text-muted-foreground mb-4">
                  {callState.status === 'connecting' && 'Connecting...'}
                  {callState.status === 'connected' && 'Connected'}
                  {callState.status === 'disconnected' && 'Call ended - closing...'}
                  {callState.status === 'error' && 'Call failed'}
                </div>
                
                {callState.status === 'connected' && (
                  <div className="flex items-center justify-center mb-4 text-sm text-muted-foreground">
                    <Clock className="w-4 h-4 mr-1" />
                    {formatDuration(callState.duration)}
                  </div>
                )}
              </div>

              <div className="flex justify-center space-x-4 mb-6">
                {callState.status === 'idle' && (
                  <button
                    onClick={initiateBrowserCall}
                    disabled={loading}
                    className="bg-blue-500 hover:bg-blue-600 text-white rounded-full p-4 disabled:opacity-50"
                  >
                    {loading ? (
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                    ) : (
                      <Phone className="h-6 w-6" />
                    )}
                  </button>
                )}

                {callState.status === 'connected' && (
                  <>
                    <button
                      onClick={toggleMute}
                      className={`rounded-full p-4 ${
                        callState.isMuted 
                          ? 'bg-red-500 hover:bg-red-600 text-white' 
                          : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                      }`}
                    >
                      {callState.isMuted ? <MicOff className="h-6 w-6" /> : <Mic className="h-6 w-6" />}
                    </button>

                    <button
                      onClick={endBrowserCall}
                      className="bg-red-500 hover:bg-red-600 text-white rounded-full p-4"
                    >
                      <PhoneOff className="h-6 w-6" />
                    </button>
                  </>
                )}

                {(callState.status === 'disconnected' || callState.status === 'error') && (
                  <button
                    onClick={onClose}
                    className="bg-gray-500 hover:bg-gray-600 text-white rounded-full p-4"
                  >
                    Close
                  </button>
                )}
              </div>



              <div className="text-xs text-muted-foreground text-center">
                {callState.status === 'idle' && (
                  <div>
                    <p>Click the blue button to start a call</p>
                    <p className="mt-1">This will connect directly to the client through your browser</p>
                  </div>
                )}
                {callState.status === 'connecting' && (
                  <p>Establishing connection...</p>
                )}
                {callState.status === 'connected' && (
                  <p>Call in progress - use the controls above to manage the call</p>
                )}
                {callState.status === 'disconnected' && (
                  <p>Call ended - modal will close automatically</p>
                )}
                {callState.status === 'error' && (
                  <p>Call failed - modal will close automatically</p>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CallModal; 